import React from 'react';
import { Box, Container, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useInView } from 'react-intersection-observer';
import CustomButton from './common/CustomButton';
import { useNavigate } from 'react-router-dom';
import AnimatedUnderline from './common/AnimatedUnderline';

const SectionWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: '80px 0',
  background: '#FFFFFF',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    height: '600px',
    background: 'linear-gradient(180deg, rgba(0,75,135,0.03) 0%, rgba(255,255,255,0) 100%)',
    transform: 'skewY(-6deg)',
    transformOrigin: 'top left',
  }
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '4rem',
  alignItems: 'start',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    gap: '3rem',
  }
}));

const TextContent = styled(Box)(({ theme }) => ({
  maxWidth: '600px',
  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  }
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: 'clamp(2rem, 5vw, 2.75rem)',
  fontWeight: 300,
  color: '#1A365D',
  marginBottom: '1.5rem',
  fontFamily: 'Poppins, sans-serif',
  letterSpacing: '-0.02em',
  textAlign: 'left',
}));

const Description = styled(Typography)(({ theme }) => ({
  fontSize: 'clamp(1rem, 1.5vw, 1.125rem)',
  lineHeight: 1.8,
  color: '#475569',
  marginBottom: '2rem',
  '& strong': {
    color: '#004B87',
    fontWeight: 500,
  }
}));

const VideoContainer = styled(Box)(() => ({
  width: '100%',
  aspectRatio: '16/9',
  borderRadius: '12px',
  overflow: 'hidden',
  position: 'relative',
  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
  '& iframe': {
    width: '100%',
    height: '100%',
    border: 'none',
  }
}));

const VideoSection = ({ title, description, videoUrl, reverse = false }) => {
  const navigate = useNavigate();
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });
  
  const getYouTubeEmbedUrl = (url) => {
    const videoId = url.split('v=')[1]?.split('&')[0];
    return `https://www.youtube.com/embed/${videoId}`;
  };

  return (
    <SectionWrapper>
      <Container 
        maxWidth="xl"
        sx={{
          px: { xs: 2, sm: 4, md: 8, lg: 12 },
        }}
      >
        <ContentWrapper>
          <TextContent>
            <Title variant="h2">
              {title}
            </Title>
            <AnimatedUnderline align="left" sx={{ marginTop: '-1.2rem', marginBottom: '1rem' }} />
            <Description>
              {description}
            </Description>
            <CustomButton
              variant="contained"
              onClick={() => navigate('/who-we-are')}
              sx={{
                height: '48px',
                fontSize: { xs: '0.9rem', sm: '1rem' },
                fontWeight: 600,
                minWidth: { xs: '180px', sm: '220px' },
                backgroundColor: '#004B87',
                '&:hover': {
                  backgroundColor: '#003966',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              En savoir plus
            </CustomButton>
          </TextContent>

          <Box ref={ref} sx={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(20px)', transition: 'all 0.6s ease' }}>
            <VideoContainer>
              <iframe
                src={getYouTubeEmbedUrl(videoUrl)}
                title="YouTube video player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </VideoContainer>
          </Box>
        </ContentWrapper>
      </Container>
    </SectionWrapper>
  );
};

export default VideoSection;
